import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { setSentryUser, clearSentryUser } from '@raporgor/utils';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  initialize: () => void;
}

// Check localStorage synchronously at module load time
const getInitialState = () => {
  try {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (token && userStr) {
      const user = JSON.parse(userStr) as User;
      // Set Sentry user context — sadece stable id + tenant + role (PII kuralı).
      setSentryUser(Sentry, {
        id: user.id,
        firmId: user.firmId ?? undefined,
        role: user.role,
      });
      return { token, user, isAuthenticated: true, isInitialized: true };
    }
  } catch {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  return { token: null, user: null, isAuthenticated: false, isInitialized: true };
};

const initialState = getInitialState();

export const useAuthStore = create<AuthState>((set) => ({
  user: initialState.user,
  token: initialState.token,
  isAuthenticated: initialState.isAuthenticated,
  isInitialized: initialState.isInitialized,

  login: (token: string, user: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    // Set Sentry user context — sadece stable id + tenant + role (PII kuralı).
    setSentryUser(Sentry, {
      id: user.id,
      firmId: user.firmId ?? undefined,
      role: user.role,
    });

    set({ token, user, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear Sentry user context
    clearSentryUser(Sentry);

    set({ token: null, user: null, isAuthenticated: false });
  },

  initialize: () => {
    // Now handled synchronously in getInitialState
    // This method kept for backward compatibility
  },
}));
